import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

interface UseBlockNavigationOptions {
  isDisabled: boolean;
  shouldBlock: boolean;
  onConfirmLeave?: () => void;
}

interface BlockedLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

export const useBlockNavigation = ({ isDisabled, shouldBlock, onConfirmLeave }: UseBlockNavigationOptions): void => {
  const history = useHistory();
  const { t } = useTranslation('app');
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!shouldBlock || isDisabled) {
      unblockRef.current?.();
      unblockRef.current = null;
      return undefined;
    }

    const unblock = history.block(((location: BlockedLocation) => {
      const confirmed = window.confirm(t('exitPromptMessage'));

      if (confirmed) {
        unblockRef.current?.();
        unblockRef.current = null;
        onConfirmLeave?.();
        history.push(location.pathname);
      }

      return false;
    }) as any);

    unblockRef.current = unblock;

    return () => {
      unblock();
    };
  }, [isDisabled, shouldBlock, history, onConfirmLeave, t]);

  useEffect(() => {
    if (!shouldBlock || isDisabled) {
      return undefined;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      Object.defineProperty(e, 'returnValue', { value: '' });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock, isDisabled]);
};
